// © Microsoft Corporation. All rights reserved.
/** @jsx h */
import h from "@msnews/wc-jsx";

import { HandledPropsKeys, KeyValueArray, UrlUtility, appEnvironment } from "@msnews/core";
import { SearchBoxHandledProps, SearchBoxProps, SearchBoxUnhandledProps } from "./SearchBox.props";

import Foundation from "@microsoft/fast-components-foundation-react";
import { MsftSearchBox } from "@fast/components";
import React from "react";
import { classNames, KeyCodes } from "@microsoft/fast-web-utilities";
import { searchBingLogo, voiceSearchIcon } from "./SearchBoxIcons";

// Used to provide refs to html elements in this component. Keep as "srchfrm" to enable autosuggest
export const searchBoxId = "srchfrm";

/**
 * The Search Box component. Renders the visual UI for Search web component.
 */
export class SearchBox extends Foundation<SearchBoxHandledProps, SearchBoxUnhandledProps, {}> {
    /** Form element */
    private formRef: HTMLFormElement;

    /**
     * Component HandledProps enumeration. Anything not enumerated here will be included in the experience root HTML tag via unhandledProps()
     */
    protected handledProps: HandledPropsKeys<SearchBoxHandledProps> = {
        action: undefined,
        autoComplete: undefined,
        autoFocus: undefined,
        dataCustomhandled: undefined,
        formParameters: undefined,
        isOnImage: undefined,
        maxLength: undefined,
        minLength: undefined,
        placeholder: undefined,
        readOnly: undefined,
        spellCheck: undefined,
        target: undefined,
        title: undefined,
        managedClasses: undefined,
        id: undefined,
        buttonTelemetryTag: undefined,
        onSubmit: undefined,
        inputTelemetryTag: undefined,
        buttonLabel: undefined,
        searchVisible: undefined,
        fakeFocused: undefined,
        searchLogoEnabled: undefined,
        showVoiceSearchButton: undefined,
        voiceSearchTelemetryObject: undefined,
        voiceSearchClickCallback: undefined
    };

    /**
     * Construct a new Search Box
     * @param props The props contract for the Search Box
     */
    constructor(props: SearchBoxProps) {
        super(props);
    }

    /**
     * Gets form ref for the form element
     */
    public componentDidMount(): void {
        this.formRef = document.getElementById(searchBoxId) as HTMLFormElement;
    }

    /**
     * Renders the component
     * @returns The Search Box JSX Element
     */
    public render(): JSX.Element {

        const { searchVisible, fakeFocused, searchLogoEnabled, showVoiceSearchButton, action, autoComplete, autoFocus, minLength, maxLength, target, placeholder, readOnly, spellCheck, title, managedClasses, isOnImage = null, id, onSubmit, buttonLabel } = this.props;

        return (
            <div className={classNames(managedClasses.searchBox, [managedClasses.searchHidden, searchVisible === false])}>
                <form
                    {...this.unhandledProps()}
                    id={searchBoxId}
                    action={action}
                    target={target || "_self"}
                    method="get"
                    data-clarity-mask={appEnvironment.ClarityEnabled ? "true" : null}>
                    <MsftSearchBox
                        autoFocus={autoFocus}
                        autoComplete={autoComplete ? "on" : "off"}
                        {...(this.props.dataCustomhandled ? { "data-customhandled": "true"} : null)}
                        id={id}
                        isOnImage={isOnImage || null}
                        name={"q"}
                        maxLength={maxLength}
                        minLength={minLength}
                        placeholder={placeholder}
                        spellCheck={spellCheck}
                        readOnly={readOnly}
                        title={title}
                        button-label={buttonLabel}
                        events={{
                            keypress: this.onKeypress,
                            submit: onSubmit
                        }}
                        props={{
                            buttonTelemetryTag: this.props.buttonTelemetryTag,
                            inputTelemetryTag: this.props.inputTelemetryTag
                        }}
                    >
                    </MsftSearchBox>
                    {showVoiceSearchButton &&
                    <button
                        className={"voiceSearchButton"}
                        id={"voice_srch_go"}
                        data-targetid = "VoiceSearchButton"
                        title={"Voice Search"}
                        type="button"
                        onClick={this.props.voiceSearchClickCallback}
                        {...(this.props.voiceSearchTelemetryObject ? this.props.voiceSearchTelemetryObject.render() : null)}
                    >
                        {voiceSearchIcon(managedClasses.voiceSearchSvg)}
                    </button>}
                    {searchLogoEnabled && searchBingLogo(managedClasses.searchBingLogo)}
                    {fakeFocused && <div className={managedClasses.fakeCursor} />}
                    {this.getHiddenInputElements()}
                    {this.getHiddenPcElementFromQueryString()}
                </form>
            </div >
        );
    }

    /**
     * Generates hidden input elements
     * @returns Hidden input elements
     */
    private getHiddenInputElements(): Array<JSX.Element> {
        if (!Array.isArray(this.props.formParameters) || !this.props.formParameters.length) {
            return;
        }

        return this.props.formParameters.map((input, index) =>
            <input key={`hiddenEl_${index}`} name={input.name} type="hidden" value={input.value} />
        );
    }

    /**
     * Generates hidden input element from query string parameter with key of pc
     * @returns Hidden input element
     */
    private getHiddenPcElementFromQueryString(): JSX.Element {

        const queryStringParams: KeyValueArray<string, string> = UrlUtility.getParamsFromUrl(location.search);
        if (!queryStringParams || !queryStringParams.length) {
            return;
        }

        const pcCode = queryStringParams.find(p => p.key.toLowerCase() === "pc");
        if (pcCode && pcCode.value) {
            return (<input key="pcHiddenElement" name={pcCode.key.toLowerCase()} type="hidden" value={pcCode.value} />);
        }
    }

    /**
     * Handler for search box keyboard events
     * @param e keyboard event
     */
    private onKeypress = (e: React.KeyboardEvent) => {
        if (!(e.keyCode === KeyCodes.enter && this.formRef)) {
            return;
        }

        const { onSubmit } = this.props;
        if (onSubmit) {
            onSubmit();
        }
        this.formRef.submit();
    };
}

export interface SearchBoxWebComponentProperties {
    /** Telemetry tag associated with the search button */
    buttonTelemetryTag?: string

    /** Telemetry tag associated with the search input */
    inputTelemetryTag?: string;
}

/**
 * This file needs to exist to augment the existing typings from MsftSearchBox
 * See https://www.typescriptlang.org/docs/handbook/declaration-merging.html#module-augmentation
 */
declare module "@fast/components" {
    interface MsftSearchBox extends React.Component<{
        autoFocus?: boolean;
        autoComplete?: string;
        id: string;
        maxLength?: number;
        minLength?: number;
        name: string;
        onChange?: any;
        isOnImage?: boolean;
        placeholder?: string;
        readOnly?: boolean;
        size?: number;
        spellCheck?: boolean;
        "button-label"?: string;
        events?: any;
        props?: SearchBoxWebComponentProperties;
    } & React.HTMLAttributes<MsftSearchBox>> { }
}